import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { enrollMemberToProgram } from '../../utils/salesforce-loyalty-api';
import { IconClose, IconMark, IconTriangle } from './Icon';
import { addToCart } from '../../utils/add-to-cart';
import { unescapeHtml } from '../../utils/string-helpers';
import { getLoyaltyOptions } from '../../utils/rewardsFunction';

/**
 * Form for creating a new loyalty member.
 * @param { Object } sectionSettings
 * @param { string } sectionSettings.firstNameLabel
 * @param { string } sectionSettings.lastNameLabel
 * @param { string } sectionSettings.emailLabel
 * @param { string } sectionSettings.marketingCheckboxLabel
 * @param { string } sectionSettings.submitBtnText
 * @param { string } sectionSettings.enableRewards
 * @param { string } sectionSettings.rewardsTitle
 * @param { string } sectionSettings.rewardsTermsLabel
 * @param { string } sectionSettings.programName
 * @returns { void }
 */
function LoyaltyRegisterForm({ sectionSettings }) {
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkCountry, setCheckCountry] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const rewardsTermsLabelUnescapedStr = unescapeHtml(sectionSettings.rewardsTermsLabel);
  const [birthdayDayRequired, setBirthdayDayRequired] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const birthdayMonthRef = useRef(null);
  const birthdayDayRef = useRef(null);
  const months = [
    { name: 'Select Month', days: 31, value: '' },
    { name: 'January', days: 31, value: '01' },
    { name: 'February', days: 29, value: '02' },
    { name: 'March', days: 31, value: '03' },
    { name: 'April', days: 30, value: '04' },
    { name: 'May', days: 31, value: '05' },
    { name: 'June', days: 30, value: '06' },
    { name: 'July', days: 31, value: '07' },
    { name: 'August', days: 31, value: '08' },
    { name: 'September', days: 30, value: '09' },
    { name: 'October', days: 31, value: '10' },
    { name: 'November', days: 30, value: '11' },
    { name: 'December', days: 31, value: '12' }
  ];
  const [selectedMonth, setSelectedMonth] = useState('');
  const [numberInput, setNumberInput] = useState('');
  const [email, setEmail] = useState(sectionSettings.customerEmail);
  const [maxDay, setMaxDay] = useState(months[0].days);
  const [showTooltip, setShowTooltip] = useState({
    dob: { show: false },
    mobileNumber: { show: false }
  });
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const containerRef = useRef(null);
  const isLoggedIn = sectionSettings.customerID !== '';

  const toggleTooltip = (position) => {
    setShowTooltip(prevState => ({
      ...prevState,
      [position]: {
        ...prevState[position],
        show: !prevState[position].show
      }
    }));
  }

  useEffect(() => {
    if ((numberInput === '' && selectedMonth !== '') || (numberInput !== '' && selectedMonth === '')) {
      setBirthdayDayRequired(true);
      if (numberInput === '') {
        birthdayDayRef.current.setCustomValidity("Please select your birthday day.");
      } else {
        birthdayDayRef.current.setCustomValidity("");
      }
      if (selectedMonth === ''){
        birthdayMonthRef.current.setCustomValidity("Please select your birthday month.");
      } else {
        birthdayMonthRef.current.setCustomValidity("");
      }
    } else {
      setBirthdayDayRequired(false);
      birthdayDayRef.current.setCustomValidity("");
      birthdayMonthRef.current.setCustomValidity("");
    }
  }, [numberInput, selectedMonth]);


  const loyaltyOptions = getLoyaltyOptions();
  if (sectionSettings.enableRewards && loyaltyOptions?.length > 1) {
    setShowRewards(true)
  }

  const handleMonthChange = (event) => {
    const selectedMonthValue = event.target.value;
    const selectedMonthObject = months.find(month => month.value === selectedMonthValue);
    setSelectedMonth(selectedMonthValue);
    setMaxDay(selectedMonthObject.days);
    if (numberInput > selectedMonthObject.days) {
      setNumberInput(selectedMonthObject.days);
    }
  }

  const handleNumberInputChange = (event) => {
    const inputNumber = parseInt(event.target.value);
    let monthInput = document.querySelector('#birthdayMonth')
    if (inputNumber) {
      if (inputNumber <= maxDay) {
        setNumberInput(inputNumber);
        setBirthdayDayRequired(true)
        monthInput?.setCustomValidity("Please select your birthday month.");
      }
    } else {
      setNumberInput('');
      if (document.querySelector('#birthdayMonth')?.value === '') {
        setBirthdayDayRequired(false)
        monthInput?.setCustomValidity("");
      }
    }
  }

  const handleEmailChange = (value) => {
    setEmail(value)
    let emailInput = document.querySelector('#emailAddress')
    if (emailRegex.test(value)) {
      emailInput.setCustomValidity('');
    } else {
      emailInput.setCustomValidity('Please enter a valid email address');
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true)
    const formData = new FormData(event.target);
    const firstName = formData.get('customer[firstName]');
    const lastName = formData.get('customer[lastName]');
    const birthdayDay = formData.get('customer[birthdayDay]');
    const birthdayMonth = formData.get('customer[birthdayMonth]');
    const mobileNumber = formatMobileAUNumber(formData.get('customer[mobileNumber]'));
    const emailAddress = formData.get('customer[emailAddress]');

    let rewardsPrice = formData.get('customer[rewards]');
    if (rewardsPrice) {
      rewardsPrice = Number(rewardsPrice)
    }
    const terms = formData.get('customer[acceptsTerms]');


    if ((birthdayDay === '' && birthdayMonth !== '') || (birthdayDay !== '' && birthdayMonth === '')) {
      setMessage('Please enter a valid birthday.')
      setMessageType('error')
      setLoading(false)
      return
    } else {
      setMessage('')
      setMessageType('')
    }

    let birthday = null;
    if (birthdayDay && birthdayMonth) {
      birthday = `${birthdayDay.padStart(2, '0')}/${birthdayMonth.padStart(2, '0')}`
    }

    let paidTier = false;
    if (rewardsPrice && rewardsPrice > 0) {
      paidTier = true;
    }

    let countryCode = 'AU';
    if (window.Shopify.shop.includes("nz")) {
      countryCode = 'NZ';
    }
    const programName = sectionSettings.programName;
    let termsAndConditions = false;
    if (terms === 'on') {
      termsAndConditions = true;
    }
    const response = await enrollMemberToProgram(firstName, lastName, mobileNumber, emailAddress, birthday, paidTier, countryCode, programName, termsAndConditions);

    if (!response) {
      setLoading(false)
      return
    }

    if (response.statuscode == '200') {
      if (paidTier) {
        await handlePaidTier()
      }
      // redirect to success page: <a href=\"https://thermomix-au-test.myshopify.com/pages/rewards-success\">"
      window.location.href = window.location.origin + '/pages/rewards-success'
    }

    if (response) {
      setMessage(response.message)
      if (response.success == true) {
        if (response.statuscode == '201') {
          setMessageType('warning');
        } else {
          setMessageType('success');
        }
      } else {
        setMessageType('error');
      }
    }
    setLoading(false)
  }

  const formatMobileAUNumber = (mobileNumber) => {
    // Remove all non-digit characters from the input
    let countryCode = '+61';
    if (window.Shopify.shop.includes("nz")) {
      countryCode = '+64';
    }
    let digitsOnly = mobileNumber.replace(/\D/g, '');
    // Replace the leading with the country code
    if (digitsOnly.startsWith('+610')) return countryCode + digitsOnly.slice(4);
    if (digitsOnly.startsWith('+640')) return countryCode + digitsOnly.slice(4);
    if (digitsOnly.startsWith('610')) return countryCode + digitsOnly.slice(3);
    if (digitsOnly.startsWith('640')) return countryCode + digitsOnly.slice(3);
    if (digitsOnly.startsWith('+61')) return countryCode + digitsOnly.slice(3);
    if (digitsOnly.startsWith('+64')) return countryCode + digitsOnly.slice(3);
    if (digitsOnly.startsWith('61')) return countryCode + digitsOnly.slice(2);
    if (digitsOnly.startsWith('64')) return countryCode + digitsOnly.slice(2);
    if (digitsOnly.startsWith('0')) return countryCode + digitsOnly.slice(1);
    return countryCode + digitsOnly;
  }

  const handleResponseMessage = (messageType, message) => {
    switch (messageType) {
      case 'error':
        return (
          <div className="container container--flush--small form-message-container error-container">
            <div className="form-message">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" fill="#C9211B" />
                <path d="M10 6L6 10" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 10L6 6" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className='strong'>{message}</p>
            </div>
          </div>
        )
      case 'warning':
        return (
          <div className="container container--flush--small form-message-container warning-container">
            <div className="form-message">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" fill="#C18B00" />
                <path d="M8 5V8.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8 11.5C8.41421 11.5 8.75 11.1642 8.75 10.75C8.75 10.3358 8.41421 10 8 10C7.58579 10 7.25 10.3358 7.25 10.75C7.25 11.1642 7.58579 11.5 8 11.5Z" fill="white" />
              </svg>
              <p className='strong'><span dangerouslySetInnerHTML={{ __html: message }}></span></p>
            </div>
          </div>
        )
    }

  }

  const handleKeyDown = (event) => {
    if (!/[a-zA-Z\- ']/i.test(event.key)) {
      event.preventDefault();
    }
  }

  const handlePaidTier = async () => {
    if (!loyaltyOptions || !selectedTier) return

    const membershipProductId = selectedTier.membership_product_id;
    try {
      const response = await addToCart(membershipProductId);
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  }

  useEffect(() => {
    if (window.Shopify.shop.includes("nz")) {
      setCheckCountry(false);
    } else {
      setCheckCountry(true);
    }
  }, [window.Shopify.shop]);

  return (
    <div className='loyalty--register-form' ref={containerRef}>
      {message ? <div>{handleResponseMessage(messageType, message)}</div> : null}
      <form onSubmit={handleSubmit} id="CustomerRegisterFormLoyalty">
        {showRewards ? (
          <div>
            <div><h2 className="h3 text-serif text-center">{sectionSettings.rewardsTitle}</h2></div>
            <div className='flex col mt-10'>
              {loyaltyOptions.length > 1 && loyaltyOptions?.map((option, i) => {
                return (
                  <div key={i} className="form__group form-radio">
                    <input
                      type="radio"
                      name="customer[rewards]"
                      id={'rewardsOptions' + i}
                      value={option.price}
                      required={i === 0}
                      defaultChecked={i === 0}
                      onClick={() => setSelectedTier(option)}
                    />
                    <label htmlFor={'rewardsOptions' + i}>
                      <div>{option.title}</div>
                      {option.more_info_link ? <a className='more_info' href={option.more_info_link}>{option.more_info_title}</a> : <div className='more_info'>{option.more_info_title}</div>}
                      <div className='price'>{option.price === '0' ? 'FREE' : option.price}</div>
                    </label>
                  </div>
                )
              })}
            </div>
          </div>
        ) : <></>}

        <div className={"form__group " + (isLoggedIn ? 'hidden' : '')}>
          <label htmlFor="FirstName">{sectionSettings.firstNameLabel}:</label>
          <input
            type="text"
            name="customer[firstName]"
            id="FirstName"
            onInput={(e) => e.target.value = e.target.value.replace(/[^a-zA-Z\- ']/g, '')}
            onKeyDown={handleKeyDown}
            pattern="[A-Za-z\- ']+"
            defaultValue={sectionSettings.customerFirstName}
            required
          />
        </div>

        <div className={"form__group " + (isLoggedIn ? 'hidden' : '')}>
          <label htmlFor="LastName">{sectionSettings.lastNameLabel}:</label>
          <input
            type="text"
            name="customer[lastName]"
            id="LastName"
            onInput={(e) => e.target.value = e.target.value.replace(/[^a-zA-Z\- ']/g, '')}
            onKeyDown={handleKeyDown}
            pattern="[A-Za-z\- ']+"
            defaultValue={sectionSettings.customerLastName}
            required
          />
        </div>

        <div className="form__group">
          <div className="wrap-icon-container">
            <label>Date of Birth (Optional) <a onClick={() => toggleTooltip('dob')}></a></label>
            <div className='icon-container'>
              <span id="icon-mark" onClick={() => toggleTooltip('dob')}><IconMark /></span>
              {showTooltip.dob.show && (
                <div className="tooltip-container">
                  <div className="tooltip">
                    <span className='icon-triangle'><IconTriangle className /></span>
                    <span className="tooltip-text">{sectionSettings.birthday_tooltip}</span>
                    <button className="close-btn" onClick={() => toggleTooltip('dob')}><IconClose /></button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-8">
            <input
              ref={birthdayDayRef}
              type="number"
              id="birthdayDay"
              name="customer[birthdayDay]"
              min="1"
              max={maxDay}
              placeholder="Enter Day"
              value={numberInput}
              onChange={handleNumberInputChange}
              required={birthdayDayRequired}
            />
            <select
              ref={birthdayMonthRef}
              id="birthdayMonth"
              name="customer[birthdayMonth]"
              value={selectedMonth}
              onChange={handleMonthChange}
              required={birthdayDayRequired}
            >
              {months.map((month, index) => (
                <option key={month.name} value={month.value}>{month.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={"form__group " + (isLoggedIn ? 'hidden' : '')}>
          <label htmlFor="emailAddress">{sectionSettings.emailLabel}:</label>
          <input
            type="email"
            name="customer[emailAddress]"
            id="emailAddress"
            spellCheck="false"
            autoComplete="off"
            autoCapitalize="off"
            value={email}
            required
            onChange={(e) => handleEmailChange(e.target.value)}
          />
        </div>

        <div className="form__group">
          <div className="wrap-icon-container">
            <label htmlFor="mobileNumber" onClick={() => toggleTooltip('mobileNumber')}>Mobile Number:</label>
            <div className='icon-container'>
              <span id="icon-mark" onClick={() => toggleTooltip('mobileNumber')}><IconMark /></span>
              {showTooltip.mobileNumber.show && (
                <div className="tooltip-container">
                  <div className="tooltip">
                    <span className='icon-triangle'><IconTriangle className /></span>
                    <span className="tooltip-text">{sectionSettings.mobile_num_tooltip}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={`input-wrapper ${checkCountry ? 'countryAU' : 'countryNZ'}`}>
            <input
              type="tel"
              name="customer[mobileNumber]"
              id="mobileNumber"
              minLength="7"
              required
            />
          </div>
        </div>

        <div className="form__group form-checkbox">
          <input
            type="checkbox"
            name="customer[acceptsTerms]"
            id="acceptsTerms"
            required
          />
          <label className="checkbox-label" htmlFor="acceptsTerms" dangerouslySetInnerHTML={{ __html: rewardsTermsLabelUnescapedStr }}></label>
        </div>

        <div className="form__group">
          <button
            type="submit"
            className="btn btn--small btn--block"
            disabled={loading}
          >
            {sectionSettings.submitBtnText}
          </button>
        </div>
        {loading && <div className="loading-overlay" id="loadingOverlay">
          <div className="spinner"></div>
        </div>
        }
      </form>
    </div>
  );
};

export default LoyaltyRegisterForm;

document.addEventListener('DOMContentLoaded', () => {
  const $sectionLoyaltyRegisterForm = document.querySelectorAll('#loyalty-register-form');

  $sectionLoyaltyRegisterForm.forEach($registerForm => {
    const data = $registerForm.getAttribute('section-settings')
    try {
      let sectionSettings = JSON.parse(data)
      ReactDOM.render(<LoyaltyRegisterForm sectionSettings={sectionSettings} />, $registerForm);
    } catch (error) {
      console.error(error);
    }
  });
});

